import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Home, ArrowRight, Construction, Clock, Wrench, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PageEnConstruction = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Date estimée de fin (dans 30 jours par exemple)
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 30);

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-gray-50">
      <Helmet>
        <title>Page en Construction - Aydees | Bientôt Disponible</title>
        <meta name="description" content="Cette page Aydees est en cours de développement. Nous travaillons pour vous offrir une expérience exceptionnelle. Revenez bientôt !" />
        <meta name="keywords" content="page en construction, développement, Aydees, bientôt disponible" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <Header />
      
      {/* Contenu principal */}
      <div className="pt-40 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          
          {/* Animation d'icônes flottantes */}
          <div className="relative mb-12">
            <div className="absolute top-0 left-1/4 animate-bounce delay-75">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                <Construction className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="absolute top-8 right-1/4 animate-bounce delay-150">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <Wrench className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 animate-bounce">
              <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-xl">
                <Clock className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          {/* Titre principal */}
          <div className="mb-8 mt-20">
            <h1 className="text-6xl md:text-8xl font-bold bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 bg-clip-text text-transparent mb-4 animate-pulse">
              🚧
            </h1>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-800 mb-4">
              Page en 
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                {" "}Construction
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Nous travaillons dur pour vous apporter une expérience exceptionnelle. 
              Cette page sera bientôt disponible !
            </p>
          </div>

          {/* Barre de progression animée */}
          <div className="mb-12">
            <div className="max-w-md mx-auto">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progression du développement</span>
                <span className="font-semibold text-orange-600">75%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                <div className="h-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 rounded-full relative animate-pulse">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-ping"></div>
                  <div className="h-full w-3/4 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 rounded-full"></div>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Nouvelle fonctionnalité bientôt disponible !
              </p>
            </div>
          </div>

          {/* Compteur de temps estimé */}
          <div className="mb-12">
            <div className="flex items-center justify-center mb-4">
              <Calendar className="w-6 h-6 text-orange-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Temps estimé restant</h3>
            </div>
            <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
              <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg text-center">
                <div className="text-2xl font-bold text-orange-600">{timeLeft.days}</div>
                <div className="text-xs text-gray-600 uppercase tracking-wide">Jours</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{timeLeft.hours}</div>
                <div className="text-xs text-gray-600 uppercase tracking-wide">Heures</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg text-center">
                <div className="text-2xl font-bold text-green-600">{timeLeft.minutes}</div>
                <div className="text-xs text-gray-600 uppercase tracking-wide">Minutes</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg text-center">
                <div className="text-2xl font-bold text-purple-600 animate-pulse">{timeLeft.seconds}</div>
                <div className="text-xs text-gray-600 uppercase tracking-wide">Secondes</div>
              </div>
            </div>
          </div>



          {/* Boutons d'action */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center group"
            >
              <Home className="w-5 h-5 mr-2" />
              Retourner à l'accueil
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>

            <button
              onClick={() => window.location.reload()}
              className="bg-white text-gray-700 px-8 py-4 rounded-full font-semibold border-2 border-gray-200 hover:border-orange-300 hover:text-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Réessayer
            </button>
          </div>

          {/* Message de contact */}
          <div className="mt-16 p-6 bg-gradient-to-r from-orange-100 to-orange-50 rounded-2xl border border-orange-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Besoin d'aide immédiate ?
            </h3>
            <p className="text-gray-600 mb-4">
              Notre équipe est là pour vous accompagner dans vos projets
            </p>
            <Link
              to="/rejoignez-nous"
              className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-700 transition-colors duration-300"
            >
              Contactez-nous
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* Formes décoratives */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-40 h-40 bg-gradient-to-r from-orange-200 to-orange-300 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-1/2 -right-24 w-56 h-56 bg-gradient-to-r from-blue-200 to-blue-300 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-32 h-32 bg-gradient-to-r from-green-200 to-green-300 rounded-full opacity-20 animate-pulse delay-500"></div>
      </div>

      <Footer />
    </div>
  );
};

export default PageEnConstruction; 